/** @format */

import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { CIRCLE_OPTIONS, REGION_OPTIONS } from "../cf-v2-create-form/config";
import { getCfStatusChipSx, loadCfFormCityOptions } from "../../utils/constants";

// Store code is "5digits-1digit-1digit" (e.g. 12345-6-7), captured as three parts.
const DEFAULT_VALUES = {
  siteName: "",
  storeCodeP1: "",
  storeCodeP2: "",
  storeCodeP3: "",
  region: "",
  circle: "",
  city: "",
};

// Resolve the three store-code parts from the record. New records carry storeCode (5 digits),
// sequenceCode (1 digit) and satelliteCode (1 digit) separately; legacy records may still hold the
// full "12345-6-7" string in storeCode alone, so split on "-" as a fallback.
const parseStoreCode = ({ storeCode, sequenceCode, satelliteCode } = {}) => {
  if (String(storeCode || "").includes("-")) {
    const parts = String(storeCode).split("-");
    return {
      storeCodeP1: (parts[0] || "").replace(/\D/g, "").slice(0, 5),
      storeCodeP2: (parts[1] || "").replace(/\D/g, "").slice(0, 1),
      storeCodeP3: (parts[2] || "").replace(/\D/g, "").slice(0, 1),
    };
  }
  return {
    storeCodeP1: String(storeCode || "").replace(/\D/g, "").slice(0, 5),
    storeCodeP2: String(sequenceCode || "").replace(/\D/g, "").slice(0, 1),
    storeCodeP3: String(satelliteCode || "").replace(/\D/g, "").slice(0, 1),
  };
};

const CfV2EditDetails = ({ open, initialValues, onClose, onSave, loading }) => {
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [cityOptions, setCityOptions] = useState([]);
  const storeCodeP2Ref = useRef(null);
  const storeCodeP3Ref = useRef(null);

  useEffect(() => {
    loadCfFormCityOptions()
      .then(setCityOptions)
      .catch(() => setCityOptions([]));
  }, []);

  useEffect(() => {
    if (open) {
      setValues({
        siteName: initialValues?.siteName || "",
        ...parseStoreCode(initialValues),
        region: initialValues?.region || "",
        circle: initialValues?.circle || "",
        city: initialValues?.city || "",
      });
    }
  }, [open, initialValues]);

  const handleChange = (field) => (e) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Digits-only store-code part; auto-advance focus to the next part once this one is full.
  const handleStoreCodePartChange = (field, maxLen, nextRef) => (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, maxLen);
    setValues((prev) => ({ ...prev, [field]: digitsOnly }));
    if (digitsOnly.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  const handleSubmit = () => {
    const siteName = values.siteName?.trim();
    if (!siteName) {
      toast.error("Site name is required");
      return;
    }
    if (!values.region) {
      toast.error("Region is required");
      return;
    }
    if (!values.circle) {
      toast.error("Circle is required");
      return;
    }
    const p1 = values.storeCodeP1?.trim() || "";
    const p2 = values.storeCodeP2?.trim() || "";
    const p3 = values.storeCodeP3?.trim() || "";
    // Each store-code part is optional and validated only when entered (sequence code in
    // particular is frequently left blank). A filled part must still be the right length.
    if (p1 && !/^\d{5}$/.test(p1)) {
      toast.error("Store code must be 5 digits");
      return;
    }
    if (p2 && !/^\d$/.test(p2)) {
      toast.error("Sequence code must be a single digit");
      return;
    }
    if (p3 && !/^\d$/.test(p3)) {
      toast.error("Satellite code must be a single digit");
      return;
    }
    onSave({
      siteName,
      storeCode: p1,
      sequenceCode: p2,
      satelliteCode: p3,
      region: values.region,
      circle: values.circle,
      city: values.city?.trim() || "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Site Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {initialValues?.cfStatus && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Status:
              </Typography>
              <Chip
                label={initialValues.cfStatus}
                size="small"
                variant="outlined"
                sx={getCfStatusChipSx(initialValues.cfStatus)}
              />
            </Stack>
          )}
          <TextField
            label="Site Name"
            value={values.siteName}
            onChange={handleChange("siteName")}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="cf-v2-edit-region-label">Region</InputLabel>
            <Select
              labelId="cf-v2-edit-region-label"
              label="Region"
              value={values.region}
              onChange={handleChange("region")}
            >
              {REGION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="cf-v2-edit-circle-label">Circle</InputLabel>
            <Select
              labelId="cf-v2-edit-circle-label"
              label="Circle"
              value={values.circle}
              onChange={handleChange("circle")}
            >
              {CIRCLE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            options={cityOptions}
            value={values.city || null}
            onChange={(_, selected) =>
              setValues((prev) => ({ ...prev, city: selected || "" }))
            }
            disablePortal
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="City" size="small" />
            )}
          />          
          {initialValues?.cfStatus === "UNDER_CONSTRUCTION" && <FormControl fullWidth>
            <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Store Code</FormLabel>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                aria-label="Store code part 1"
                value={values.storeCodeP1}
                onChange={handleStoreCodePartChange("storeCodeP1", 5, storeCodeP2Ref)}
                size="small"
                placeholder="12345"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "\\d*",
                  maxLength: 5,
                  style: { textAlign: "center", letterSpacing: 2 },
                }}
                sx={{ width: 110 }}
              />
              <Typography variant="h6" sx={{ color: "text.secondary" }}>
                -
              </Typography>
              <TextField
                aria-label="Store code part 2"
                inputRef={storeCodeP2Ref}
                value={values.storeCodeP2 || 'X'}
                onChange={handleStoreCodePartChange("storeCodeP2", 1, storeCodeP3Ref)}
                size="small"
                placeholder="6"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "\\d*",
                  maxLength: 1,
                  style: { textAlign: "center" },
                }}
                disabled
                sx={{ width: 56 }}
              />
              <Typography variant="h6" sx={{ color: "text.secondary" }}>
                -
              </Typography>
              <TextField
                aria-label="Store code part 3"
                inputRef={storeCodeP3Ref}
                value={values.storeCodeP3}
                onChange={handleStoreCodePartChange("storeCodeP3", 1, null)}
                size="small"
                placeholder="7"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "\\d*",
                  maxLength: 1,
                  style: { textAlign: "center" },
                }}
                sx={{ width: 56 }}
              />
            </Stack>
          </FormControl>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CfV2EditDetails;
